// pms-core / domain — GENERATING THE GRID from a season calendar × rate profile.
//
// Step 4 of PMS-SEASON-CALENDAR-BRIEF, and the piece that frees the rest of the
// code from `RateParameterSet`. Pure: it PROPOSES rows and writes nothing, so
// the « never overwrite a hand-adjusted day » guard (R4, migration 0117) cannot
// be forgotten here by accident — it belongs to whatever stores the result, and
// this function has no way to reach the grid at all.
//
// ── Two objects, because they run on two clocks ─────────────────────────────
//
//   calendar — WHEN, and what the Chambre Double Standard costs then. Its
//              structure is re-cut yearly from Éducation nationale zone dates;
//              its BASE moves continuously, set against competing hotels and
//              expected occupancy (operator, 2026-08-24).
//   profile  — HOW that base becomes every other price: per-(season × category)
//              markups, both meal pairs, the length-of-stay ladder. Near-static.
//
// Welded together they were « unduly complicated » (the operator's words, and
// the reason this module exists). Composition runs one way — calendar × profile
// → grid — and neither object names the other.
//
// ── What moved, and what did not ────────────────────────────────────────────
//
// The ARITHMETIC is unchanged and deliberately NOT re-implemented:
// `mealComponent` and `tierFor` are imported from `rateGenerator.ts`, so a
// change to how a board is priced moves both paths together. What changed is
// only where the numbers come from — `period.baseCents` instead of
// `set.seasons[season].base`.

import type {
  Cents,
  IsoDate,
  Rate,
  RatePlan,
  RatePlanId,
  RoomType,
  RoomTypeId,
} from './types.js';
import type { ChildMealPrices, LengthOfStayTier, MealPrices, RateSeason } from './rateParameters.js';
import { datesBetween, mealComponent, tierFor } from './rateGenerator.js';

// ── The two objects ──────────────────────────────────────────────────────────

/**
 * One dated stretch of a fiscal year, carrying ITS OWN base price.
 *
 * Named `CalendarPeriod` rather than `SeasonPeriod` because
 * `rateParameters.ts` still exports the latter for the object this replaces.
 * The collision is temporary — the old type dies with
 * `pms_rate_parameter_set` (step 3) — but a silent re-export shadowing would
 * not be, so the two are kept distinguishable while both exist.
 *
 * `from`/`to` are INCLUSIVE: a period is a stretch of civil calendar, and an
 * exclusive end reads as an off-by-one to everyone who checks it against a
 * printed year. (A stay's departure is exclusive; a period is not a stay.)
 */
export interface CalendarPeriod {
  id: number;
  from: IsoDate;
  to: IsoDate;
  description: string;
  /** « A/B », « A », or null. Documentation of WHY the period exists — never an
   *  input to any calculation, because the tier is a commercial judgement about
   *  this hotel and not a fact derivable from a zone table. */
  zones: string | null;
  season: RateSeason;
  /** The Chambre Double Standard, accommodation only, for these dates. */
  baseCents: Cents;
}

/** When the hotel is SHUT — its own dimension, independent of the tier. The
 *  Découpage sheet prices 2026-03-03 → 2026-07-03 straight through an April the
 *  hotel is closed, which is the proof that these cannot be one field. */
export interface SeasonClosure {
  from: IsoDate;
  to: IsoDate;
  reason: string;
}

/** One fiscal year, 1 December → 30 November, named by its END year. */
export interface SeasonCalendar {
  year: number;
  periods: CalendarPeriod[];
  closures: SeasonClosure[];
}

/** Per-category numbers. `occupancy` is deliberately absent — it is
 *  `pms_room_type.maxOccupancy`, reference data about the building rather than
 *  a pricing parameter. */
export interface ProfileRoomType {
  /** Above the period's base, per season. Never one global figure: Très Haute
   *  hikes only the two SUP types. */
  markup: Record<RateSeason, Cents>;
  /** The authored « à partir de » shown on the site, and a free floor (R11). */
  minPosted: Cents | null;
}

/**
 * HOW the hotel prices. Nothing dated lives here — migration 0118 enforces that
 * with a CHECK refusing a profile that carries `seasonCalendar` or `seasons`,
 * because the weld would otherwise return silently and simply work, once, for
 * one year.
 *
 * ⚠ `minStayBySeason`, not `seasons`. The obvious name is the one the database
 * forbids: a key called `seasons` is exactly what the anti-weld CHECK rejects.
 * The constraint doing its job at design time rather than at runtime is the
 * point.
 */
export interface RateProfile {
  name: string;
  roomTypes: Record<RoomTypeId, ProfileRoomType>;
  /** OTA-facing meal prices — the grid and the OTA listing (R6). */
  mealsBooking: MealPrices;
  /** The direct engine's meal prices (R12). Never used for the grid. */
  mealsDirect: MealPrices;
  /** A child's direct meals by age band (PMS-PHONE-BOOKING-BRIEF P7/P8). Never
   *  used for the grid either: the channels cannot be sent ages. Absent on the
   *  profiles published before PB1 — a child then eats at `mealsDirect`, which
   *  never undersells. */
  childMealsDirect?: ChildMealPrices | null;
  lengthOfStayTiers: LengthOfStayTier[];
  /** Minimum nights sellable per tier — 3 in Très Haute. Stays with the tier
   *  because that is what a tier still MEANS once the base has moved to the
   *  period: the markup and the minimum stay, not the price. */
  minStayBySeason: Record<RateSeason, number>;
}

// ── Reading the calendar ─────────────────────────────────────────────────────

/**
 * Which period covers a date, or `null` when none does.
 *
 * Null is a real answer and must stay one: a calendar legitimately stops before
 * the next year is authored, and a generator that silently fell back to some
 * default would fill the grid with confident wrong prices. The caller decides
 * what an uncovered date means.
 *
 * **Later periods win on overlap**, matching `seasonOn` and the database's own
 * note: it is what lets a correction be stapled onto the end of a year without
 * rewriting it.
 */
export function periodOn(calendar: SeasonCalendar, date: IsoDate): CalendarPeriod | null {
  let found: CalendarPeriod | null = null;
  for (const p of calendar.periods) {
    if (date >= p.from && date <= p.to) found = p;
  }
  return found;
}

/** Whether the hotel is shut on a date. Independent of the period — a closed
 *  date usually sits inside a perfectly ordinary priced stretch. */
export function isClosedOn(calendar: SeasonCalendar, date: IsoDate): boolean {
  return calendar.closures.some((c) => date >= c.from && date <= c.to);
}

/** The fiscal year's bounds — 1 December of `year - 1` to 30 November of
 *  `year`. Both inclusive; 365 days in a common year. */
export function fiscalYearRange(year: number): { from: IsoDate; to: IsoDate } {
  return { from: `${year - 1}-12-01`, to: `${year}-11-30` };
}

// ── The generator ────────────────────────────────────────────────────────────

export interface GenerateGridInput {
  calendar: SeasonCalendar;
  profile: RateProfile;
  /** The plans to price. Channel CHILDREN are skipped here rather than by the
   *  caller: a derived plan has nowhere to hold a price (R15), and leaving the
   *  filter to every caller is how one of them eventually forgets. */
  plans: RatePlan[];
  roomTypes: RoomType[];
  /** INCLUSIVE both ends, and a RANGE rather than the whole year on purpose:
   *  the base moves all year, so re-pricing February must not rewrite
   *  December. */
  from: IsoDate;
  to: IsoDate;
}

export interface GenerateGridResult {
  /** Proposed rows. Nothing is stored — see the header. */
  rates: Rate[];
  /** Dates the calendar does not cover. Reported rather than defaulted. */
  uncoveredDates: IsoDate[];
  /** Categories the profile carries no markup for, so nothing could be priced
   *  for them. Named rather than silently omitted. */
  unpricedRoomTypeIds: RoomTypeId[];
  /** Dates inside a closure. Their rows ARE generated, carrying `closed`. */
  closedDates: IsoDate[];
  /** Plans skipped for being channel children. */
  skippedPlanIds: RatePlanId[];
}

/**
 * Expand a calendar × profile into proposed grid rows.
 *
 * ```
 * price   = period.base + markup[category][period.season]
 *         + occupancy × meals(board)        ← the BOOKING pair, always
 * minStay = profile.minStayBySeason[period.season]
 * closed  = the date falls inside a closure
 * ```
 *
 * ⚠ `mealsBooking`, never `mealsDirect`. The grid is what reaches the channel,
 * and the OTA listing shows what an OTA guest is charged (R6). The Direct pair
 * prices the direct quote and nothing else (R12) — reading the wrong one here
 * would under-price every OTA rate while looking entirely plausible.
 *
 * **A closed date is GENERATED, with `closed: true`**, rather than omitted.
 * Under O11 a wrongly-sellable date is the failure with no ceiling, and a row
 * that exists and is stop-sold is unambiguous where a missing row is not: a
 * date with no row is indistinguishable from one nobody has priced yet.
 */
export function generateGrid(input: GenerateGridInput): GenerateGridResult {
  const { calendar, profile, plans, roomTypes, from, to } = input;

  const pricedTypes = roomTypes.filter((rt) => profile.roomTypes[rt.id] !== undefined);
  const unpricedRoomTypeIds = roomTypes
    .filter((rt) => profile.roomTypes[rt.id] === undefined)
    .map((rt) => rt.id);

  const parents = plans.filter((p) => p.derivesFromId === null);
  const skippedPlanIds = plans.filter((p) => p.derivesFromId !== null).map((p) => p.id);

  const rates: Rate[] = [];
  const uncoveredDates: IsoDate[] = [];
  const closedDates: IsoDate[] = [];

  for (const date of datesBetween(from, to)) {
    const period = periodOn(calendar, date);
    if (period === null) {
      uncoveredDates.push(date);
      continue;
    }
    const closed = isClosedOn(calendar, date);
    if (closed) closedDates.push(date);

    const minStay = profile.minStayBySeason[period.season];

    for (const plan of parents) {
      for (const rt of pricedTypes) {
        const markup = profile.roomTypes[rt.id]!.markup[period.season];
        rates.push({
          ratePlanId: plan.id,
          roomTypeId: rt.id,
          date,
          priceCents: period.baseCents + markup + mealComponent(plan.board, rt.maxOccupancy, profile.mealsBooking),
          minStay,
          closed,
          // Deliberately NOT set. A proposed row has no provenance until it is
          // stored, and asserting one here would be the generator claiming to
          // know something only the grid can answer (0117).
        });
      }
    }
  }

  return { rates, uncoveredDates, unpricedRoomTypeIds, closedDates, skippedPlanIds };
}

// ── The direct quote, against the same two objects ───────────────────────────

/**
 * What a DIRECT booking pays per night, for a stay of `nights`.
 *
 * Two independent percentages (R2): the stay is discounted harder than the
 * meals, and `discountMeals` may be 0. Meals come from `mealsDirect` — the one
 * place the Direct pair is correct, and the inversion of §7's original headline
 * that R12 recorded.
 */
export function directRate(
  profile: RateProfile,
  period: CalendarPeriod,
  roomType: RoomType,
  board: RatePlan['board'],
  nights: number,
): Cents | null {
  const rt = profile.roomTypes[roomType.id];
  if (rt === undefined) return null;

  const room = period.baseCents + rt.markup[period.season];
  const meals = mealComponent(board, roomType.maxOccupancy, profile.mealsDirect);
  const tier = tierFor(profile, nights);
  const stayOff = tier?.discountStay ?? 0;
  const mealsOff = tier?.discountMeals ?? 0;

  // Rounded ONCE, at the end — never per component, which would let a rounding
  // crumb ride on every line.
  return Math.round(room * (1 - stayOff) + meals * (1 - mealsOff));
}

// ── Validation ───────────────────────────────────────────────────────────────

/**
 * Structural problems in a calendar, as French strings.
 *
 * Deliberately NOT a boolean and NOT an exception: a calendar with a gap is a
 * legitimate work-in-progress that the operator is part-way through authoring.
 * The caller decides whether a gap matters — it does before generating a full
 * year, and does not before saving.
 */
export function validateCalendar(calendar: SeasonCalendar): string[] {
  const problems: string[] = [];
  const sorted = [...calendar.periods].sort((a, b) => (a.from < b.from ? -1 : 1));

  for (const p of sorted) {
    if (p.to < p.from) {
      problems.push(`Période « ${p.description || p.from} » : la fin précède le début.`);
    }
    if (p.baseCents <= 0) {
      problems.push(`Période « ${p.description || p.from} » : tarif de base non renseigné.`);
    }
  }

  for (let i = 1; i < sorted.length; i += 1) {
    const prev = sorted[i - 1]!;
    const cur = sorted[i]!;
    if (cur.from <= prev.to) {
      problems.push(
        `Chevauchement : « ${prev.description || prev.from} » et « ${cur.description || cur.from} ». La période la plus tardive l’emporte.`,
      );
    }
  }

  const { from, to } = fiscalYearRange(calendar.year);
  const covered = new Set<string>();
  for (const p of calendar.periods) for (const d of datesBetween(p.from, p.to)) covered.add(d);
  const gaps = datesBetween(from, to).filter((d) => !covered.has(d));
  if (gaps.length > 0) {
    // The gap is usually the closure, and saying so turns a warning into an
    // explanation: the 2026 sheet stops on 30 October precisely because
    // November is shut.
    const closedGaps = gaps.filter((d) => isClosedOn(calendar, d)).length;
    problems.push(
      `${gaps.length} jour(s) de l’exercice sans période tarifaire` +
        (closedGaps > 0 ? ` (dont ${closedGaps} en fermeture).` : '.'),
    );
  }

  return problems;
}
